/**
 * Сумма прописью для счёта на оплату.
 *
 * Своя реализация, а не пакет из npm: задача маленькая и полностью
 * определённая, а готовые пакеты либо заброшены, либо тянут лишние
 * зависимости в бэкенд, который печатает бухгалтерский документ.
 *
 * Особенности русской грамматики, которые здесь учтены:
 *  - женский род для тысяч («одна тысяча», «две тысячи») и для копеек;
 *  - мужской род для рублей, миллионов и миллиардов («один рубль», «два миллиона»);
 *  - числа 11–14 всегда берут форму множественного числа («одиннадцать рублей»).
 */
const ONES_MASCULINE = [
    '',
    'один',
    'два',
    'три',
    'четыре',
    'пять',
    'шесть',
    'семь',
    'восемь',
    'девять',
];
const ONES_FEMININE = [
    '',
    'одна',
    'две',
    'три',
    'четыре',
    'пять',
    'шесть',
    'семь',
    'восемь',
    'девять',
];
const TEENS = [
    'десять',
    'одиннадцать',
    'двенадцать',
    'тринадцать',
    'четырнадцать',
    'пятнадцать',
    'шестнадцать',
    'семнадцать',
    'восемнадцать',
    'девятнадцать',
];
const TENS = [
    '',
    '',
    'двадцать',
    'тридцать',
    'сорок',
    'пятьдесят',
    'шестьдесят',
    'семьдесят',
    'восемьдесят',
    'девяносто',
];
const HUNDREDS = [
    '',
    'сто',
    'двести',
    'триста',
    'четыреста',
    'пятьсот',
    'шестьсот',
    'семьсот',
    'восемьсот',
    'девятьсот',
];
const RUBLE_FORMS = ['рубль', 'рубля', 'рублей'];
const KOPECK_FORMS = ['копейка', 'копейки', 'копеек'];
const THOUSAND_FORMS = ['тысяча', 'тысячи', 'тысяч'];
const MILLION_FORMS = ['миллион', 'миллиона', 'миллионов'];
const BILLION_FORMS = ['миллиард', 'миллиарда', 'миллиардов'];
/** Выбирает форму существительного по числу. */
export function pluralize(count, forms) {
    const abs = Math.abs(count) % 100;
    // 11–14 — исключение: несмотря на последнюю цифру, форма всегда «рублей».
    if (abs >= 11 && abs <= 14)
        return forms[2];
    switch (abs % 10) {
        case 1:
            return forms[0];
        case 2:
        case 3:
        case 4:
            return forms[1];
        default:
            return forms[2];
    }
}
/** Записывает группу из трёх разрядов словами. */
function groupToWords(group, feminine) {
    const words = [];
    const hundreds = Math.floor(group / 100);
    const remainder = group % 100;
    if (hundreds > 0)
        words.push(HUNDREDS[hundreds]);
    if (remainder >= 10 && remainder <= 19) {
        words.push(TEENS[remainder - 10]);
        return words;
    }
    const tens = Math.floor(remainder / 10);
    const ones = remainder % 10;
    if (tens > 0)
        words.push(TENS[tens]);
    if (ones > 0) {
        words.push(((feminine ? ONES_FEMININE : ONES_MASCULINE)[ones]));
    }
    return words;
}
/** Целое число словами. `feminine` управляет родом последнего разряда. */
export function integerToWords(value, feminine = false) {
    if (!Number.isInteger(value) || value < 0) {
        throw new Error(`Ожидалось неотрицательное целое, получено ${value}`);
    }
    if (value === 0)
        return 'ноль';
    const billions = Math.floor(value / 1_000_000_000);
    const millions = Math.floor((value % 1_000_000_000) / 1_000_000);
    const thousands = Math.floor((value % 1_000_000) / 1000);
    const rest = value % 1000;
    const words = [];
    if (billions > 0) {
        words.push(...groupToWords(billions, false), pluralize(billions, BILLION_FORMS));
    }
    if (millions > 0) {
        words.push(...groupToWords(millions, false), pluralize(millions, MILLION_FORMS));
    }
    if (thousands > 0) {
        // Тысяча — женского рода: «одна тысяча», а не «один тысяча».
        words.push(...groupToWords(thousands, true), pluralize(thousands, THOUSAND_FORMS));
    }
    if (rest > 0) {
        words.push(...groupToWords(rest, feminine));
    }
    return words.join(' ');
}
/**
 * Сумма прописью в формате, который ожидает бухгалтерия:
 * «Пятьдесят тысяч рублей 00 копеек».
 *
 * Копейки печатаются цифрами — так принято в счетах и исключает разночтения.
 */
export function kopecksToWords(kopecks) {
    if (!Number.isInteger(kopecks)) {
        throw new Error(`Сумма должна быть целым числом копеек, получено ${kopecks}`);
    }
    const negative = kopecks < 0;
    const abs = Math.abs(kopecks);
    const rubles = Math.floor(abs / 100);
    const rest = abs % 100;
    const rublesWords = integerToWords(rubles);
    const capitalized = rublesWords.charAt(0).toUpperCase() + rublesWords.slice(1);
    const text = `${capitalized} ${pluralize(rubles, RUBLE_FORMS)} ${String(rest).padStart(2, '0')} ${pluralize(rest, KOPECK_FORMS)}`;
    return negative ? `Минус ${text.charAt(0).toLowerCase()}${text.slice(1)}` : text;
}
//# sourceMappingURL=rubles.js.map